/**
 * src/pages/Clientes.jsx
 * Lista de clientes con formulario de creación.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge, TIER_COLOR } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import { Input, Select } from '../components/ui/Input'
import { useClientes, useCreateCliente } from '../hooks/useClientes'

const TIER_OPTS = [
  { value: 'basic', label: 'Basic' },
  { value: 'pro', label: 'Pro' },
  { value: 'premium', label: 'Premium' },
]

const FREQ_OPTS = [
  { value: '24h', label: 'Cada 24h' },
  { value: '48h', label: 'Cada 48h' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'manual', label: 'Manual' },
]

// Tiendas CM soportadas por el comparador (mantener en sync con el wizard y
// TIENDAS_SOPORTADAS del backend).
const FAMILIA_OPTS = [
  { value: 'alimentos2',  label: 'Alimentos',  url: 'https://conveniomarco2.mercadopublico.cl/alimentos2' },
  { value: 'ferreteria2', label: 'Ferretería', url: 'https://conveniomarco2.mercadopublico.cl/ferreteria2' },
]

function CreateModal({ onClose }) {
  const createMutation = useCreateCliente()
  const [form, setForm] = useState({
    cliente_id: '', nombre: '', email: '',
    tier: 'basic', familia_cm: 'alimentos2',
    url_base: 'https://conveniomarco2.mercadopublico.cl/alimentos2',
    empresa_rut: '', empresa_nombre: '', empresa_seller_code: '',
  })
  const [error, setError] = useState('')

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }))
  }

  async function handleSubmit() {
    setError('')
    if (!form.cliente_id || !form.nombre || !form.empresa_rut || !form.empresa_seller_code) {
      setError('Completa los campos requeridos')
      return
    }
    try {
      await createMutation.mutateAsync({
        cliente_id: form.cliente_id.toLowerCase().replace(/\s+/g, '_'),
        nombre: form.nombre,
        email: form.email || undefined,
        tier: form.tier,
        familia_cm: form.familia_cm,
        url_base: form.url_base,
        empresas_propias: [{
          rut: form.empresa_rut,
          nombre: form.empresa_nombre || form.nombre,
          seller_code: form.empresa_seller_code,
        }],
        competidores: [],
      })
      onClose()
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al crear el cliente')
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100, padding: '20px',
    }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--color-surface)', border: '1px solid var(--color-border)',
        borderRadius: '12px', width: '100%', maxWidth: '520px',
        boxShadow: 'var(--shadow-lg)', animation: 'fadeIn 0.15s ease-out',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text)' }}>
            Nuevo cliente
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-3)', fontSize: '20px', lineHeight: 1, padding: '2px',
          }}>×</button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input label="ID CLIENTE *" value={form.cliente_id} onChange={set('cliente_id')}
              placeholder="ej. nutrihouse" />
            <Select label="TIER" value={form.tier} onChange={set('tier')} options={TIER_OPTS} />
          </div>
          <Input label="NOMBRE *" value={form.nombre} onChange={set('nombre')}
            placeholder="Nombre de la empresa" />
          <Input label="EMAIL" value={form.email} onChange={set('email')}
            type="email" placeholder="contacto@empresa.cl" />
          <Select label="FAMILIA CM" value={form.familia_cm}
            onChange={e => {
              const fam = FAMILIA_OPTS.find(x => x.value === e.target.value)
              setForm(f => ({ ...f, familia_cm: fam.value, url_base: fam.url }))
            }}
            options={FAMILIA_OPTS} />

          <div style={{
            padding: '14px', background: 'var(--color-surface-2)',
            border: '1px solid var(--color-border)', borderRadius: '8px',
          }}>
            <div style={{
              fontSize: '11px', fontWeight: 500, color: 'var(--color-text-3)',
              fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '12px',
            }}>
              EMPRESA PROPIA
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Input label="RUT *" value={form.empresa_rut} onChange={set('empresa_rut')}
                placeholder="76.000.000-0" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <Input label="NOMBRE" value={form.empresa_nombre} onChange={set('empresa_nombre')}
                  placeholder="Razón social" />
                <Input label="SELLER CODE *" value={form.empresa_seller_code} onChange={set('empresa_seller_code')}
                  placeholder="1234567" />
              </div>
            </div>
          </div>

          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: '6px', padding: '10px 12px',
              fontSize: '12px', color: 'var(--color-error)', fontFamily: 'var(--font-mono)',
            }}>
              {error}
            </div>
          )}
        </div>

        <div style={{
          padding: '14px 24px', borderTop: '1px solid var(--color-border)',
          display: 'flex', gap: '10px', justifyContent: 'flex-end',
        }}>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            loading={createMutation.isPending}
          >
            Crear cliente
          </Button>
        </div>
      </div>
    </div>
  )
}

const COLUMNS = [
  { key: 'cliente_id', label: 'ID', mono: true, nowrap: true },
  { key: 'nombre', label: 'Nombre' },
  { key: 'familia_cm', label: 'Familia CM', mono: true },
  {
    key: 'tier', label: 'Tier', width: '90px',
    render: (v) => <Badge color={TIER_COLOR[v] || 'gray'}>{v}</Badge>,
  },
  {
    key: 'activo', label: 'Estado', width: '80px',
    render: (v) => <Badge color={v ? 'green' : 'gray'} dot>{v ? 'activo' : 'inactivo'}</Badge>,
  },
]

export default function Clientes() {
  const navigate = useNavigate()
  const [showCreate, setShowCreate] = useState(false)
  const [soloActivos, setSoloActivos] = useState(true)
  const { data: clientes = [], isLoading } = useClientes(soloActivos)

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Clientes"
        subtitle={`${clientes.length} clientes${soloActivos ? ' activos' : ''}`}
        action={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setSoloActivos(v => !v)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '12px', color: 'var(--color-text-3)',
                fontFamily: 'var(--font-mono)', padding: '6px 10px',
              }}
            >
              {soloActivos ? 'Ver todos' : 'Solo activos'}
            </button>
            <Button
              size="sm"
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
              onClick={() => setShowCreate(true)}
            >
              Nuevo cliente
            </Button>
          </div>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        <Card>
          <Table
            columns={COLUMNS}
            data={clientes}
            loading={isLoading}
            emptyMessage="Sin clientes — crea el primero"
            onRowClick={row => navigate(`/clientes/${row.cliente_id}`)}
          />
        </Card>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} />}
    </div>
  )
}
